/**
 * The retention window, in words a reader can act on.
 *
 * One module so that "when will this be deleted?" is answered identically on
 * the dossier card, on the history screen and in the deletion dialog. The date
 * itself comes from the server (`delete_after`) — the frontend never computes a
 * window from `retention_days`, because the row's own column is the thing the
 * sweeper actually reads.
 */
import type { TranslationKey } from '@/i18n'

const MS_PER_DAY = 24 * 60 * 60 * 1000

/** Swiss date, no time: the window is a day, not a minute. */
const swissDate = new Intl.DateTimeFormat('de-CH', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

export interface RetentionPhrase {
  key: TranslationKey
  vars: Record<string, string | number>
  /** true once the window has closed — the next sweep removes the dossier. */
  due: boolean
}

export function formatDate(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : swissDate.format(date)
}

/**
 * Which sentence to show for a `delete_after`.
 *
 * Three cases, deliberately distinct: no window recorded (a dossier from before
 * the window existed — say so rather than implying "kept forever"), the window
 * has closed (the sweep will take it), and the ordinary countdown.
 */
export function retentionPhrase(
  deleteAfter: string | null | undefined,
  now: Date = new Date(),
): RetentionPhrase {
  if (!deleteAfter) return { key: 'retention.none', vars: {}, due: false }
  const date = new Date(deleteAfter)
  if (Number.isNaN(date.getTime())) return { key: 'retention.none', vars: {}, due: false }

  const formatted = formatDate(date)
  const days = Math.ceil((date.getTime() - now.getTime()) / MS_PER_DAY)
  if (days <= 0) return { key: 'retention.due', vars: { date: formatted }, due: true }
  if (days === 1) return { key: 'retention.deleteToday', vars: { date: formatted }, due: false }
  if (days <= 7) {
    return { key: 'retention.deleteIn', vars: { days, date: formatted }, due: false }
  }
  return { key: 'retention.deleteOn', vars: { date: formatted }, due: false }
}
